import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../nhrc_apis/ApiService";

const INITIAL_FORM = {
  programTitle: "",
  shortDescription: "",
  trainingMode: "",
  capacity: "",
  trainerName: "",
  contactEmail: "",
  programCategory: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  location: "",
};

const UpdateSkills = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ── Detect edit/preview mode from navigation state ──
  const jobFromState = location.state?.job || null;
  const isEditMode = !!jobFromState;

  // ── On mount: if edit mode, fetch full training by ID ──
  useEffect(() => {
    if (!isEditMode) return;

    const fetchById = async () => {
      setIsFetching(true);
      try {
        const res = await ApiService.TrainingAPI.getTrainingById(jobFromState.id);
        const d = res?.data?.data || res?.data || {};

        setFormData({
          programTitle:     d.title             || d.program_title      || "",
          shortDescription: d.short_description || "",
          trainingMode:     d.training_mode     || "",
          capacity:         d.capacity          || "",
          trainerName:      d.trainer_name      || "",
          contactEmail:     d.contact_email     || "",
          programCategory:  d.program_category  || "",
          startDate:        d.start_date        || "",
          endDate:          d.end_date          || "",
          startTime:        d.start_time        ? d.start_time.slice(0, 5) : "",
          endTime:          d.end_time          ? d.end_time.slice(0, 5)   : "",
          location:         d.location          || "",
        });

        if (d.banner_image) {
          setBannerPreview(d.banner_image);
        }
      } catch (err) {
        console.error("Fetch by ID error:", err);
        toast.error("Failed to load training details.");
      } finally {
        setIsFetching(false);
      }
    };

    fetchById();
  }, [isEditMode, jobFromState?.id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "bannerImg" && files && files[0]) {
      const file = files[0];
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const requiredFields = [
      { key: "programTitle",     label: "Program Title" },
      { key: "shortDescription", label: "Short Description" },
      { key: "trainingMode",     label: "Training Mode" },
      { key: "programCategory",  label: "Program Category" },
      { key: "capacity",         label: "Capacity" },
      { key: "trainerName",      label: "Trainer Name" },
      { key: "contactEmail",     label: "Contact Email" },
      { key: "startDate",        label: "Start Date" },
      { key: "endDate",          label: "End Date" },
      { key: "startTime",        label: "Start Time" },
      { key: "endTime",          label: "End Time" },
      { key: "location",         label: "Location" },
    ];

    for (const field of requiredFields) {
      if (!formData[field.key]?.toString().trim()) {
        toast.error(`⚠️ ${field.label} is required`);
        return false;
      }
    }

    // Banner required only when creating — in edit mode existing banner is fine
    if (!isEditMode && !bannerFile) {
      toast.error("⚠️ Banner image is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contactEmail)) {
      toast.error("⚠️ Please enter a valid contact email");
      return false;
    }

    if (Number(formData.capacity) <= 0) {
      toast.error("⚠️ Capacity must be greater than 0");
      return false;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error("⚠️ End date cannot be before start date");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      let uploadedBannerUrl = bannerPreview; // keep existing banner if not changed

      // ── STEP 1: Upload new banner only if a new file was selected ──
      if (bannerFile) {
        console.log("━━━━ Uploading banner image ━━━━");
        const imageFormData = new FormData();
        imageFormData.append("file", bannerFile);
        imageFormData.append("membership_id", "training");
        imageFormData.append("file_type", "banner_image");

        const uploadResponse = await ApiService.UploadAPI.uploadFile(imageFormData);
        console.log("UPLOAD RESPONSE:", uploadResponse?.data);

        const rawPath =
          uploadResponse?.data?.file_path       ||
          uploadResponse?.data?.url             ||
          uploadResponse?.data?.fileUrl         ||
          uploadResponse?.data?.file_url        ||
          uploadResponse?.data?.path            ||
          uploadResponse?.data?.image_url       ||
          uploadResponse?.data?.data?.url       ||
          uploadResponse?.data?.data?.file_path ||
          uploadResponse?.data?.data?.file_url  ||
          uploadResponse?.data?.data?.path      ||
          "";

        if (!rawPath) {
          toast.error("❌ Image upload failed. No URL returned.");
          setIsSubmitting(false);
          return;
        }

        const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
        uploadedBannerUrl = rawPath.startsWith("http")
          ? rawPath
          : `${BASE_URL}/${rawPath}`.replace(/([^:]\/)\/+/g, "$1");

        console.log("FINAL BANNER URL:", uploadedBannerUrl);
      }

      // ── STEP 2: Build payload ──
      const payload = {
        title:             formData.programTitle.trim(),
        short_description: formData.shortDescription.trim(),
        training_mode:     formData.trainingMode,
        program_category:  formData.programCategory,
        capacity:          Number(formData.capacity),
        trainer_name:      formData.trainerName.trim(),
        contact_email:     formData.contactEmail.trim().toLowerCase(),
        start_date:        formData.startDate,
        end_date:          formData.endDate,
        start_time:        formData.startTime + ":00",
        end_time:          formData.endTime + ":00",
        location:          formData.location.trim(),
        banner_image:      uploadedBannerUrl,
      };

      console.log("PAYLOAD:", JSON.stringify(payload, null, 2));

      // ── STEP 3: Create or Update ──
      if (isEditMode) {
        console.log("━━━━ Updating training ID:", jobFromState.id, "━━━━");
        const updateResponse = await ApiService.TrainingAPI.updateTraining(jobFromState.id, payload);
        console.log("UPDATE RESPONSE:", updateResponse?.data);

        toast.success("✅ Training program updated successfully!", {
          autoClose: 2000,
          onClose: () => navigate("/dashboard/UstTotalApplications"),
        });
      } else {
        console.log("━━━━ Creating new training ━━━━");
        const trainingResponse = await ApiService.TrainingAPI.createTraining(payload);
        console.log("CREATE RESPONSE:", trainingResponse?.data);

        setFormData(INITIAL_FORM);
        setBannerFile(null);
        setBannerPreview("");

        toast.success("🎉 Training program created successfully!", {
          autoClose: 2000,
          onClose: () => navigate("/dashboard/UstTotalApplications"),
        });
      }
    } catch (error) {
      console.error("━━━━ SUBMIT ERROR ━━━━", error);
      const message =
        error?.response?.data?.message          ||
        error?.response?.data?.detail?.[0]?.msg ||
        error?.response?.data?.detail           ||
        error?.response?.data?.error            ||
        "Something went wrong. Please try again.";
      toast.error(`❌ ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading state while fetching existing training ──
  if (isFetching) {
    return (
      <div className="container mt-5 text-center py-5">
        <div className="spinner-border text-danger" role="status" />
        <p className="mt-3 text-muted">Loading training details...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-5">
      <h4 className="mb-4 text-danger fw-bold text-center">
        {isEditMode
          ? "NHRC — Edit / Preview Training Program"
          : "NHRC — Update Skills Training (Admin)"}
      </h4>

      <form
        className="p-4 border rounded shadow-sm bg-white"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Row 1 — Title & Description */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Program Title *</label>
            <input
              type="text"
              name="programTitle"
              value={formData.programTitle}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter program title"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Short Description *</label>
            <textarea
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              className="form-control"
              placeholder="Brief description of the training"
              rows={2}
            />
          </div>
        </div>

        {/* Row 2 — Mode & Category */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Training Mode *</label>
            <select
              name="trainingMode"
              value={formData.trainingMode}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select mode</option>
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
              <option value="Hybrid">Online / Offline (Hybrid)</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Program Category *</label>
            <select
              name="programCategory"
              value={formData.programCategory}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select category</option>
              <option value="Technical Skills">Technical Skills</option>
              <option value="Soft Skills">Soft Skills</option>
              <option value="Professional Development">Professional Development</option>
              <option value="HR & Management">HR &amp; Management</option>
              <option value="Others">Others</option>
            </select>
          </div>
        </div>

        {/* Row 3 — Capacity & Trainer */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Capacity *</label>
            <input
              type="number"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              className="form-control"
              placeholder="Max participants"
              min={1}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Trainer / Host Name *</label>
            <input
              type="text"
              name="trainerName"
              value={formData.trainerName}
              onChange={handleChange}
              className="form-control"
              placeholder="Trainer or host name"
            />
          </div>
        </div>

        {/* Row 4 — Email & Banner */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Contact Email *</label>
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              className="form-control"
              placeholder="contact@example.com"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Upload Banner Image {!isEditMode && "*"}
              {isEditMode && (
                <span className="text-muted fw-normal ms-1" style={{ fontSize: "12px" }}>
                  (leave empty to keep current)
                </span>
              )}
            </label>
            <input
              type="file"
              name="bannerImg"
              accept="image/png, image/jpeg"
              className="form-control"
              onChange={handleChange}
            />
            <small className="text-muted">JPG / PNG • Recommended 350×300</small>
            {bannerPreview && (
              <div className="mt-2 d-flex align-items-center gap-2">
                <img
                  src={bannerPreview}
                  alt="Banner preview"
                  className="rounded border"
                  style={{ height: "80px", objectFit: "cover" }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    setBannerFile(null);
                    setBannerPreview("");
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 5 — Dates */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">End Date *</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="form-control"
              min={formData.startDate || undefined}
            />
          </div>
        </div>

        {/* Row 6 — Times */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Start Time *</label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">End Time *</label>
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        {/* Row 7 — Location */}
        <div className="mb-4">
          <label className="form-label fw-semibold">Location *</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="form-control"
            placeholder="Venue or online link"
          />
        </div>

        {/* Buttons */}
        <div className="d-flex gap-3 align-items-center">
          <button
            type="submit"
            className="btn btn-success px-5"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                {isEditMode ? "Updating..." : "Submitting..."}
              </>
            ) : isEditMode ? (
              "UPDATE"
            ) : (
              "SUBMIT"
            )}
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={() => navigate("/dashboard/UstTotalApplications")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateSkills;